import Button from '@pelckmans/elementis/components/button';
import ButtonGroup from '@pelckmans/elementis/components/button-group';
import List from '@pelckmans/elementis/components/list';
import Pill from '@pelckmans/elementis/components/pill';
import { useToast } from '@pelckmans/elementis/components/toast';
import SmartschoolIcon from '@pelckmans/elementis/icons/smartschool';
import SuccessIcon from '@pelckmans/elementis/icons/success';
import WarningIcon from '@pelckmans/elementis/icons/warning';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SmartschoolSchool, SmartschoolIdentity as TSmartschoolIdentity } from '../../../api/my-account';
import { TOAST_ID } from '../../../constants/ToastIds';
import AccountContext from '../../../contexts/AccountContext';
import ModalContext from '../../../contexts/ModalContext';
import { buildGroupProps } from '../../../utils/elementisUtils';
import useLoginProvider from '../hooks/useLoginProvider';
import SmartschoolDecouplingModal from '../modals/SmartschoolDecouplingModal';
import ConfigureOneRosterModal from './ConfigureOneRosterModal';

export default function SmartschoolIdentity({
  identity,
}: {
  identity: TSmartschoolIdentity & { school: SmartschoolSchool };
}) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { disconnect, verifySmartschoolAdmin } = useLoginProvider();
  const { showModal } = useContext(ModalContext);
  const hasAdminPrivileges = identity.school ? identity.school.userIsAdmin : false;
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    account: { primaryEmail, verifiedEmail, existingPassword },
  } = useContext(AccountContext);

  const openSmsModal = searchParams.get('openSmsModal');
  const code = searchParams.get('code');
  const error = searchParams.get('error');

  useEffect(() => {
    if (identity.school && openSmsModal === identity.school.id && error) {
      const errors = {
        user_not_admin: 'USER_NOT_ADMIN',
        user_mismatch: 'USER_MISMATCH',
      };

      const errorKey = error in errors ? errors[error as keyof typeof errors] : 'FALLBACK';
      addToast({
        id: TOAST_ID.SMARTSCHOOL_VERIFICATION_ERROR,
        title: t('PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.CONFIGURE_ONEROSTER_MODAL.TOASTS.TITLE'),
        children: t(`PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.CONFIGURE_ONEROSTER_MODAL.TOASTS.${errorKey}`),
        variant: 'danger',
      });

      setSearchParams(prev => {
        prev.delete('openSmsModal');
        prev.delete('error');
        return prev;
      });
    }

    if (identity.school && openSmsModal === identity.school.id && code) {
      showModal(<ConfigureOneRosterModal id="configure-one-roster-modal" school={identity.school} />);
    }
  }, [openSmsModal, error, t, code, showModal, identity.school, addToast, setSearchParams]);

  function handleDisconnect() {
    const isAccountComplete = Boolean(primaryEmail && verifiedEmail && existingPassword);

    if (!isAccountComplete) {
      addToast({
        id: TOAST_ID.SMARTSCHOOL_DECOUPLING_ERROR,
        variant: 'warning',
        title: t('TOAST.WARNING.TITLE'),
        children: t('PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.TOASTS.PROFILE_INCOMPLETE_DECOUPLING.TEXT'),
      });
    } else {
      showModal(
        <SmartschoolDecouplingModal
          id="smartschool-decoupling-modal"
          onConfirm={() => disconnect(identity.id, 'smartschool', language)}
        />,
      );
    }
  }

  const subtext = identity.school ? `${identity.school?.name} • ${identity.url}` : `${identity.url}`;

  return (
    <List.Item
      key={identity.id}
      title={identity.fullName}
      subText={subtext}
      icon={<SmartschoolIcon />}
      pillGroupProps={
        hasAdminPrivileges && identity.school
          ? {
              role: 'group',
              'aria-label': 'Admin',
              children: identity.school.oneRoster?.enabled ? (
                <Pill prominence="success" icon={<SuccessIcon />}>
                  {t('PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.ONEROSTER_ACTIVE')}
                </Pill>
              ) : (
                <Pill prominence="warning" icon={<WarningIcon />}>
                  {t('PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.ONEROSTER_INACTIVE')}
                </Pill>
              ),
            }
          : undefined
      }
      actionArea={
        <ButtonGroup
          {...buildGroupProps(
            <>
              {hasAdminPrivileges && identity.school && (
                <Button
                  prominence="neutral"
                  size="sm"
                  onClick={() => verifySmartschoolAdmin(language, identity.school)}
                >
                  {t('PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.MANAGE_ONEROSTER')}
                </Button>
              )}
              <Button prominence="neutral" onClick={handleDisconnect} size="sm">
                {t('PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.DISCONNECT')}
              </Button>
            </>,
          )}
          size="sm"
        />
      }
    />
  );
}
